import { Button, Card, Space, Table, Typography, Segmented, Badge, Input, DatePicker, Select, Col, Row, Form, Tooltip, Dropdown, Menu } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectService } from '../../Services/Manage/ProjectService';
import { ExpenseServices } from '../../Services/Manage/ExpenseServices';
import { EditOutlined, EyeOutlined, ProjectOutlined, ClockCircleOutlined, CheckCircleOutlined, PauseCircleOutlined, WalletOutlined, BankOutlined, ExclamationCircleOutlined, DownOutlined, DownloadOutlined, CalendarOutlined } from '@ant-design/icons';
import { DepartmentEmpServices } from '../../Services/Manage/DepartmentEmpServices';
import { UserService } from '../../Services/Manage/UserService';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { InvoiceService } from '../../Services/Manage/InvoiceService';


dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { RangePicker } = DatePicker;

const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const UTIL_COLORS = ["#1677ff", "#52c41a", "#722ed1", "#fa8c16"];



const STATUS_COLORS = {
  Planning: "#8c8c8c",
  Live: "#1677ff",
  Hold: "#fa8c16",
  Pending: "#faad14",
  Archive: "#722ed1",
  Completed: "#52c41a",
};

const PROJECT_STATUS_PALETTE = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#13c2c2", "#eb2f96", "#f5222d", "#8c8c8c"];

// Same StatCard visual language as EnquiryPage.jsx — 11px label, 22px value, 36px icon circle.
const StatCard = ({ title, value, sub, color, bg, icon }) => (
  <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{sub}</div>}
      </div>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
        {icon}
      </div>
    </div>
  </Card>
);

const ProjectsPage = () => {

  const perm = getPermission('/projects');
  const viewAll = perm.view_all === true;
  const navigate = useNavigate();
  const [allProject, setAllProject] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('Live');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
const [selectedPriority, setSelectedPriority] = useState(null);
  const [dateFilterRange, setDateFilterRange] = useState(null); // null = All Time (default)
  const [dateFilterLabel, setDateFilterLabel] = useState('All Time');

  // project_no -> { collected, pending }
  const [paymentMap, setPaymentMap] = useState({});
  const [loadingPayments, setLoadingPayments] = useState(false);

  // [{ name, occupied, total, pct }] — department-wise engineer utilization
  const [deptUtilization, setDeptUtilization] = useState([]);

  // user_id -> name, for resolving pro_manager/pro_engineer ids to display names
  const [userMap, setUserMap] = useState({});

  const getAllProjects = async () => {
    try {
      const request = await ProjectService.getAllProject();
      let rows = request.data || [];

      if (!viewAll) {
        const user = await UserService.getOmeUser();
        const uid = String(user.uid).trim();
        rows = rows.filter((p) => {
          let engineers = p.pro_engineer;
          if (typeof engineers === "string") {
            try { engineers = JSON.parse(engineers); } catch { engineers = []; }
          }
          const isEngineer = (engineers || []).some((id) => String(id).trim() === uid);
          return (
            String(p.pro_manager ?? "").trim() === uid ||
            String(p.added_by ?? "").trim() === uid ||
            isEngineer
          );
        });
      }

      setAllProject(rows);
      setFilteredProjects(rows);
      loadPayments(rows);
      loadTeamUtilization(rows);
      loadUserMap();
    } catch (error) {
      console.error(error);
    }
  };

  const loadUserMap = async () => {
    try {
      const res = await UserService.getAllUsers();
      const map = {};
      (res.data || []).forEach((u) => {
        if (u.user_id) map[String(u.user_id)] = u.name;
      });
      setUserMap(map);
    } catch (error) {
      console.error(error);
    }
  };

  // Team Utilization = for each department, headcount (from users table) vs
  // engineers currently occupied on Live projects (from pro_engineer arrays,
  // which store user_id — same field CreateProjects.jsx writes to).
  const loadTeamUtilization = async (rows) => {
    try {
      const [deptRes, userRes] = await Promise.all([
        DepartmentEmpServices.getAllDepartment(),
        UserService.getAllUsers(),
      ]);
      const departments = deptRes.data || [];
      const users = userRes.data || [];

      const totalByDept = {};
      const userDeptMap = {};
      users.forEach((u) => {
        const deptIds = String(u.department_id || "").split(",").map((id) => id.trim()).filter(Boolean);
        deptIds.forEach((id) => { totalByDept[id] = (totalByDept[id] || 0) + 1; });
        // one engineer counted under their first department only, so occupied
        // totals don't double-count someone in more than one dept bucket.
        if (deptIds[0]) userDeptMap[String(u.user_id)] = deptIds[0];
      });

      // Count engineers as occupied across all projects, regardless of status.
      const occupiedIds = new Set();
      rows.forEach((p) => {
        let engineers = p.pro_engineer;
        if (typeof engineers === "string") {
          try { engineers = JSON.parse(engineers); } catch { engineers = []; }
        }
        (engineers || []).forEach((id) => occupiedIds.add(String(id)));
      });

      const occupiedByDept = {};
      occupiedIds.forEach((id) => {
        const dept = userDeptMap[id];
        if (dept) occupiedByDept[dept] = (occupiedByDept[dept] || 0) + 1;
      });
      const utilization = departments
        .map((d) => {
          const total = totalByDept[String(d.id)] || 0;
          const occupied = occupiedByDept[String(d.id)] || 0;
          const pct = total ? Math.round((occupied / total) * 100) : 0;
          return { name: d.name, occupied, total, pct };
        })
        .filter((d) => d.total > 0)
        .sort((a, b) => b.pct - a.pct)
        .slice(0, 4);
      setDeptUtilization(utilization);
    } catch (error) {
      console.error(error);
    }
  };

  const loadPayments = async (rows) => {
    setLoadingPayments(true);
    try {
      const invoiceRes = await InvoiceService.getAllInvoice();
      const allInvoices = invoiceRes?.data || [];

      // invoices grouped by the quotation qu_no that generated them
      const invoicesBySourceQu = {};
      allInvoices.forEach((inv) => {
        const key = inv.source_qu_no;
        if (!key) return;
        if (!invoicesBySourceQu[key]) invoicesBySourceQu[key] = [];
        invoicesBySourceQu[key].push(inv);
      });
      const invoiceByOwnQu = {};
      allInvoices.forEach((inv) => {
        if (inv.qu_no) invoiceByOwnQu[inv.qu_no] = inv;
      });

      const map = {};
      rows.forEach((p) => {
        if (!p.pro_no) return;

        let collected = 0;

        if (p.pro_source === "Quotation" && p.qu_no) {
          const linkedInvoices = invoicesBySourceQu[p.qu_no] || [];
          collected = linkedInvoices.reduce(
            (s, inv) => s + (inv.payments || []).reduce((a, pay) => a + Number(pay.paid_amt || 0), 0),
            0
          );
        } else if (p.pro_source === "Direct Invoice" && p.qu_no) {
          const inv = invoiceByOwnQu[p.qu_no];
          collected = (inv?.payments || []).reduce((s, pay) => s + Number(pay.paid_amt || 0), 0);
        }
        // Manual / no source doc — no invoice exists, so collected stays 0.
        // (No fallback to project-level payment tracking.)

        const pending = Math.max(Number(p.pro_value || 0) - collected, 0);
        map[p.pro_no] = { collected, pending };
      });
      setPaymentMap(map);
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    getAllProjects();
  }, []);

  const preStatusFiltered = useMemo(() => {
    let filtered = allProject;

    if (searchText) {
      filtered = filtered.filter(project =>
        Object.values(project).some(value =>
          value?.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(project => {
        let projectDate = dayjs(project.pro_start_date, "DD-MM-YYYY", true);
        if (!projectDate.isValid()) projectDate = dayjs(project.pro_start_date);
        return projectDate.isValid() && projectDate.isBetween(start, end, 'day', '[]');
      });
    }

    if (selectedDepartments.length > 0) {
      filtered = filtered.filter(project =>
        selectedDepartments.includes(project.department_name)
      );
    }

    if (selectedManager) {
      filtered = filtered.filter(project => project.pro_manager === selectedManager);
    }

    if (selectedCustomer) {
      filtered = filtered.filter(project => project.pro_customer === selectedCustomer);
    }

if (selectedPriority) {
      filtered = filtered.filter(project => project.pro_priority === selectedPriority);
    }

    if (dateFilterRange) {
      filtered = filtered.filter(project => {
        const d = dayjs(project.added_on);
        return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
      });
    }

    return filtered;
  }, [allProject, searchText, dateRange, selectedDepartments, selectedManager, selectedCustomer, selectedPriority, dateFilterRange]);

  const statusCounts = useMemo(() => {
    const counts = { Planning: 0, Live: 0, Hold: 0, Pending: 0, Archive: 0, Completed: 0 };
    preStatusFiltered.forEach((project) => {
      if (counts.hasOwnProperty(project.pro_status)) counts[project.pro_status]++;
    });
    return counts;
  }, [preStatusFiltered]);

  const projectStatusColorMap = useMemo(() => {
    const uniqueStatuses = [...new Set(allProject.map(p => p.pro_visibility).filter(Boolean))];
    const map = {};
    uniqueStatuses.forEach((s, i) => { map[s] = PROJECT_STATUS_PALETTE[i % PROJECT_STATUS_PALETTE.length]; });
    return map;
  }, [allProject]);

  const projectStatusCounts = useMemo(() => {
    const map = {};
    preStatusFiltered.forEach((p) => {
      const key = p.pro_visibility || "Not Set";
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [preStatusFiltered]);

  // ---- Money aggregates from real payment data ----
  const totalProjectValue = useMemo(
    () => preStatusFiltered.reduce((s, p) => s + Number(p.pro_value || 0), 0),
    [preStatusFiltered]
  );

  const totalCollected = useMemo(
    () => preStatusFiltered.reduce((s, p) => s + (paymentMap[p.pro_no]?.collected || 0), 0),
    [preStatusFiltered, paymentMap]
  );

  const totalPending = useMemo(
    () => preStatusFiltered.reduce((s, p) => s + (paymentMap[p.pro_no]?.pending ?? Number(p.pro_value || 0)), 0),
    [preStatusFiltered, paymentMap]
  );

  // Overdue = pending amount on projects whose target end date has already passed.
  const totalOverdue = useMemo(
    () => preStatusFiltered.reduce((s, p) => {
      const pending = paymentMap[p.pro_no]?.pending ?? Number(p.pro_value || 0);
      let endDate = dayjs(p.pro_target_end_date, "DD-MM-YYYY", true);
      if (!endDate.isValid()) endDate = dayjs(p.pro_target_end_date);
      const isOverdue = endDate.isValid() && endDate.isBefore(dayjs(), 'day');
      return isOverdue && pending > 0 ? s + pending : s;
    }, 0),
    [preStatusFiltered, paymentMap]
  );

  const collectedPct = totalProjectValue ? Math.round((totalCollected / totalProjectValue) * 100) : 0;
  const pendingPct = totalProjectValue ? Math.round((totalPending / totalProjectValue) * 100) : 0;
  const overduePct = totalProjectValue ? Math.round((totalOverdue / totalProjectValue) * 100) : 0;

  const statusDistData = useMemo(() =>
    Object.entries(projectStatusCounts)
      .map(([name, value]) => ({ name, value, color: projectStatusColorMap[name] || "#bfbfbf" })),
    [projectStatusCounts, projectStatusColorMap]
  );
  const statusDistTotal = statusDistData.reduce((a, b) => a + b.value, 0);

  const valueByTypeData = useMemo(() => {
    const map = {};
    preStatusFiltered.forEach((p) => {
      const key = p.pro_type || "Unassigned";
      map[key] = (map[key] || 0) + Number(p.pro_value || 0);
    });
    const palette = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#13c2c2", "#eb2f96"];
    return Object.entries(map).map(([name, value], i) => ({ name, value, color: palette[i % palette.length] }));
  }, [preStatusFiltered]);
  const valueByTypeTotal = valueByTypeData.reduce((a, b) => a + b.value, 0);

  const departmentOptions = [...new Set(allProject.map(p => p.department_name).filter(Boolean))].map(d => ({ label: d, value: d }));
  const managerOptions = [...new Set(allProject.map(p => p.pro_manager).filter(Boolean))].map(m => ({ label: userMap[String(m)] || m, value: m })); const customerOptions = [...new Set(allProject.map(p => p.pro_customer).filter(Boolean))].map(c => ({ label: c, value: c }));
  const priorityOptions = [...new Set(allProject.map(p => p.pro_priority).filter(Boolean))].map(p => ({ label: p, value: p }));

  const REPORT_COLUMNS = [
    { label: "Project No" },
    { label: "Project Name" },
    { label: "Customer" },
    { label: "Project Value (Rs.)" },
    { label: "Collected (Rs.)" },
    { label: "Pending (Rs.)" },
    { label: "Project Manager" },
    { label: "Start Date" },
    { label: "End Date" },
    { label: "Priority" },
    { label: "Status" },
  ];

  const buildReportRows = () => filteredProjects.map((p) => {
    const pay = paymentMap[p.pro_no] || {};
    const parseDate = (v) => {
      if (!v) return '-';
      let d = dayjs(v, "DD-MM-YYYY", true);
      if (!d.isValid()) d = dayjs(v);
      return d.isValid() ? d.format("DD-MM-YYYY") : '-';
    };
    return {
      "Project No": p.pro_no || "-",
      "Project Name": p.pro_name || "-",
      "Customer": p.pro_customer || "-",
      "Project Value (Rs.)": Number(p.pro_value || 0),
      "Collected (Rs.)": pay.collected || 0,
      "Pending (Rs.)": pay.pending ?? Number(p.pro_value || 0),
      "Project Manager": userMap[String(p.pro_manager)] || p.pro_manager || "-",
      "Start Date": parseDate(p.pro_start_date),
      "End Date": parseDate(p.pro_target_end_date),
      "Priority": p.pro_priority || "-",
      "Status": p.pro_visibility || "-",
    };
  });

  const dateStamp = () => dayjs().format("YYYY-MM-DD");

  const handleExportExcel = () => {
    const rows = buildReportRows();
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projects Report");
    XLSX.writeFile(wb, `Projects-Report-${dateStamp()}.xlsx`);
  };

  const handleExportPDF = () => {
    const rows = buildReportRows();
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Projects Report", 14, 14);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
    autoTable(doc, {
      startY: 26,
      head: [REPORT_COLUMNS.map((c) => c.label)],
      body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 88, 180] },
    });
    doc.save(`Projects-Report-${dateStamp()}.pdf`);
  };

  const exportMenuItems = [
    { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
    { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
  ];

  useEffect(() => {
    setFilteredProjects(preStatusFiltered);
  }, [preStatusFiltered]);

  const daysLeftLabel = (endDate) => {
    if (!endDate) return { text: "-", color: "#999" };
    let d = dayjs(endDate, "DD-MM-YYYY", true);
    if (!d.isValid()) d = dayjs(endDate);
    if (!d.isValid()) return { text: "-", color: "#999" };
    const diff = d.startOf('day').diff(dayjs().startOf('day'), 'day');
    if (diff < 0) return { text: `Overdue ${Math.abs(diff)} Days`, color: "#f5222d" };
    if (diff === 0) return { text: "Due Today", color: "#fa8c16" };
    return { text: `${diff} Days Left`, color: "#52c41a" };
  };

  const columns = [
    { title: 'SI No', dataIndex: 'key', width: '5%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
    { title: 'Project No', dataIndex: 'pro_no', key: 'pro_no' },
    {
      title: 'Project Name',
      dataIndex: 'pro_name',
      key: 'pro_name',
      render: (v, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.pro_customer || '-'}</div>
        </div>
      ),
    },
    {
      title: 'Project Value',
      dataIndex: 'pro_value',
      key: 'pro_value',
      align: 'right',
      render: (v) => `₹ ${Number(v || 0).toLocaleString('en-IN')}`,
    },
    {
      title: 'Collected / Pending',
      key: 'payment_status',
      render: (_, record) => {
        const p = paymentMap[record.pro_no];
        if (!p) return loadingPayments ? <Typography.Text type="secondary" style={{ fontSize: 11 }}>Loading...</Typography.Text> : "-";
        return (
          <>
            <div style={{ fontSize: 12 }}>Collected: <strong style={{ color: '#52c41a' }}>₹{p.collected.toLocaleString('en-IN')}</strong></div>
            <div style={{ fontSize: 12 }}>Pending: <strong style={{ color: '#fa8c16' }}>₹{p.pending.toLocaleString('en-IN')}</strong></div>
          </>
        );
      },
    },
    {
      title: 'Project Manager',
      dataIndex: 'pro_manager',
      key: 'pro_manager',
      render: (v) => userMap[String(v)] || v || '-',
    },
    {
      title: 'Created By',
      dataIndex: 'added_by',
      key: 'added_by',
      render: (v) => userMap[String(v)] || v || '-',
    },
   {
      title: 'Engineer',
      dataIndex: 'pro_engineer',
      key: 'pro_engineer',
      render: (v) => {
        let engineers = v;
        if (typeof engineers === "string") {
          try { engineers = JSON.parse(engineers); } catch { engineers = []; }
        }
        if (!Array.isArray(engineers) || engineers.length === 0) return '-';

        const names = engineers.map((id) => userMap[String(id)] || id);

        if (names.length <= 2) return names.join(', ');

        return (
          <Tooltip title={names.join(', ')}>
            <span>{names.slice(0, 2).join(', ')}...</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Start / End Date',
      key: 'dates',
      render: (_, record) => {
        const parseDate = (v) => {
          if (!v) return null;
          let d = dayjs(v, "DD-MM-YYYY", true);
          if (!d.isValid()) d = dayjs(v);
          return d.isValid() ? d : null;
        };
        const start = parseDate(record.pro_start_date);
        const end = parseDate(record.pro_target_end_date);
        return (
          <div style={{ fontSize: 12 }}>
            <div>{start ? start.format("DD-MM-YYYY") : '-'}</div>
            <div style={{ color: '#888' }}>{end ? end.format("DD-MM-YYYY") : '-'}</div>
          </div>
        );
      },
    },
    {
      title: 'Days Left',
      key: 'days_left',
      render: (_, record) => {
        const d = daysLeftLabel(record.pro_target_end_date);
        return <span style={{ color: d.color, fontWeight: 600, fontSize: 12 }}>{d.text}</span>;
      },
    },
    { title: 'Priority', dataIndex: 'pro_priority', key: 'pro_priority', render: (v) => v || '-' },
    {
      title: 'Status',
      dataIndex: 'pro_visibility',
      key: 'pro_visibility',
      render: (v) => (
        <span style={{
          background: `${projectStatusColorMap[v] || "#bfbfbf"}1a`,
          color: projectStatusColorMap[v] || "#595959",
          padding: '3px 10px', borderRadius: 6, fontWeight: 600, fontSize: 12,
        }}>
          {v || '-'}
        </span>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'Action',
      key: 'Action',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
            <Button
              onClick={() => navigate('/projects/view', { state: { id: record.id, action: 'View', enableEdit: false } })}
              icon={<EyeOutlined />} size="middle" disabled={!perm.read}
            />
          </Tooltip>
          <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
            <Button
              onClick={() => navigate('/projects/edit', { state: { id: record.id, action: 'Edit', enableEdit: true } })}
              icon={<EditOutlined />} size="middle" disabled={!perm.edit}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const styles = {
    badge: { backgroundColor: "#1b1660ff", fontSize: 14, minWidth: 24, height: 24, lineHeight: "24px", marginLeft: 10 },
  };

  const segmentOptions = [
    { label: (<span>All <Badge count={preStatusFiltered.length || 0} style={styles.badge} /></span>), value: "All" },
    { label: (<span>Live <Badge count={statusCounts["Live"] || 0} style={styles.badge} /></span>), value: "Live" },
    { label: (<span>Hold <Badge count={statusCounts["Hold"] || 0} style={styles.badge} /></span>), value: "Hold" },
    { label: (<span>Pending <Badge count={statusCounts["Pending"] || 0} style={styles.badge} /></span>), value: "Pending" },
    { label: (<span>Archive <Badge count={statusCounts["Archive"] || 0} style={styles.badge} /></span>), value: "Archive" },
    { label: (<span>Completed <Badge count={statusCounts["Completed"] || 0} style={styles.badge} /></span>), value: "Completed" },
  ];

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }}>
      {/* Header — mirrors EnquiryPage's header pattern */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Project Dashboard</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Project Dashboard</Typography.Text>
        </div>
        <Space wrap>
          <Button
            size="middle" type="primary"
            style={{ background: "#16a34a", borderColor: "#16a34a" }}
            onClick={() => navigate('/project-expenses', { state: { action: 'Edit', enableEdit: true } })}
          >
            Create Project Expenses
          </Button>
          <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
            <Button
              size="middle" type="primary" disabled={!perm.add}
              onClick={() => navigate('/projects/create', { state: { action: 'Edit', enableEdit: true } })}
              style={{ background: '#1677ff', borderColor: '#1677ff' }}
            >
              Add Project
            </Button>
          </Tooltip>
  <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
          </Dropdown>
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu
                onClick={({ key }) => {
                  let range = null;
                  let label = 'All Time';
                  if (key === 'today') {
                    range = [dayjs().startOf('day'), dayjs().endOf('day')];
                    label = 'Today';
                  } else if (key === 'last7') {
                    range = [dayjs().subtract(6, 'day').startOf('day'), dayjs()];
                    label = 'Last 7 Days';
                  } else if (key === 'last30') {
                    range = [dayjs().subtract(29, 'day').startOf('day'), dayjs()];
                    label = 'Last 30 Days';
                  } else if (key === 'lastMonth') {
                    range = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                    label = 'Last Month';
                  } else if (key === 'thisYear') {
                    range = [dayjs().startOf('year'), dayjs()];
                    label = 'This Year';
                  } else if (key === 'all') {
                    range = null;
                    label = 'All Time';
                  }
                  setDateFilterRange(range);
                  setDateFilterLabel(label);
                }}
                items={[
                  { key: 'all', label: <span><CalendarOutlined style={{ marginRight: 6 }} />All Time</span> },
                  { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                  { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                  { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                  { key: 'lastMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last Month</span> },
                  { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                ]}
              />
            }
          >
            <Button size="middle" icon={<DownOutlined />}>
              More Actions ({dateFilterLabel})
            </Button>
          </Dropdown>
        </Space>
      </div>

      {/* Stat cards — row 1: status counts */}
      <div style={{ padding: '8px 16px 0' }}>
        <Row gutter={[8, 8]} style={{ marginBottom: 6 }}>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Live Projects" value={statusCounts.Live} sub="Active Projects" color="#1677ff" bg="#f0f7ff" icon={<ProjectOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Pending Projects" value={statusCounts.Pending} sub="Not Started" color="#fa8c16" bg="#fff7e6" icon={<ClockCircleOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Completed Projects" value={statusCounts.Completed} sub="Successfully Completed" color="#722ed1" bg="#f9f0ff" icon={<CheckCircleOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="On Hold Projects" value={statusCounts.Hold} sub="Temporarily Hold" color="#f5222d" bg="#fff1f0" icon={<PauseCircleOutlined />} />
          </Col>
        </Row>

        {/* Stat cards — row 2: money */}
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Total Project Value" value={`₹ ${totalProjectValue.toLocaleString('en-IN')}`} sub="All Projects" color="#1677ff" bg="#f0f7ff" icon={<WalletOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Total Collected" value={`₹ ${totalCollected.toLocaleString('en-IN')}`} sub={`${collectedPct}% of Total`} color="#52c41a" bg="#f6ffed" icon={<BankOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Total Pending Amount" value={`₹ ${totalPending.toLocaleString('en-IN')}`} sub={`${pendingPct}% of Total`} color="#fa8c16" bg="#fff7e6" icon={<ClockCircleOutlined />} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Total Overdue Amount" value={`₹ ${totalOverdue.toLocaleString('en-IN')}`} sub={`${overduePct}% of Total`} color="#f5222d" bg="#fff1f0" icon={<ExclamationCircleOutlined />} />
          </Col>
        </Row>
      </div>

      {/* Charts row */}
      <div style={{ padding: '6px 16px 0' }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Project Status Distribution</Typography.Text>
              {statusDistTotal === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                  <span style={{ fontSize: 28 }}>📊</span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                  <div style={{ width: 110, height: 110, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${statusDistTotal ? Math.round((v / statusDistTotal) * 100) : 0}%)`, n]} />
                        <Pie data={statusDistData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={50} paddingAngle={2}>
                          {statusDistData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1 }}>
                    {statusDistData.map((e, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: e.color, fontSize: 14 }}>●</span>
                          <span style={{ color: '#333' }}>{e.name}</span>
                        </span>
                        <span style={{ fontWeight: 700, color: '#333' }}>{statusDistTotal ? Math.round((e.value / statusDistTotal) * 100) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Revenue by Project Type</Typography.Text>
              {valueByTypeTotal === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                  <span style={{ fontSize: 28 }}>📊</span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                  <div style={{ width: 110, height: 110, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`₹ ${Number(v).toLocaleString('en-IN')}`, n]} />
                        <Pie data={valueByTypeData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={50} paddingAngle={2}>
                          {valueByTypeData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1 }}>
                    {valueByTypeData.map((e, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: e.color, fontSize: 14 }}>●</span>
                          <span style={{ color: '#333' }}>{e.name}</span>
                        </span>
                        <span style={{ fontWeight: 700, color: '#333', whiteSpace: 'nowrap' }}>₹ {e.value.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Team Utilization</Typography.Text>
              {deptUtilization.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                  <span style={{ fontSize: 28 }}></span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                  {deptUtilization.map((d, i) => {
                    const color = UTIL_COLORS[i % UTIL_COLORS.length];
                    return (
                      <Col span={Math.floor(24 / Math.min(deptUtilization.length, 4)) || 6} key={i}>
                        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 6px', textAlign: 'center', height: '100%' }}>
                          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{d.name}</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.2 }}>{d.pct}%</div>
                          <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>Occupied</div>
                          <div style={{ height: 5, background: '#f0f0f0', borderRadius: 3, marginBottom: 6 }}>
                            <div style={{ width: `${d.pct}%`, height: '100%', background: color, borderRadius: 3 }} />
                          </div>
                          <div style={{ fontSize: 11, color: '#888', textAlign: 'right' }}>{d.occupied} / {d.total}</div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card>
          </Col>
        </Row>
      </div>

      {/* Segment filter */}


      {/* Filters */}
      <div style={{ padding: '6px 16px' }}>
        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Search Project" layout="vertical" style={{ marginBottom: 0 }}>
                <Input
                  allowClear
                  placeholder="Search by project name, no..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Project Manager" layout="vertical" style={{ marginBottom: 0 }}>
                <Select allowClear placeholder="All Project Managers" options={managerOptions}
                  value={selectedManager} onChange={setSelectedManager} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Customer" layout="vertical" style={{ marginBottom: 0 }}>
                <Select allowClear placeholder="All Customers" options={customerOptions}
                  value={selectedCustomer} onChange={setSelectedCustomer} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item label="Priority" layout="vertical" style={{ marginBottom: 0 }}>
                <Select allowClear placeholder="All Priority" options={priorityOptions}
                  value={selectedPriority} onChange={setSelectedPriority} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item label="Department" layout="vertical" style={{ marginBottom: 0 }}>
                <Select allowClear mode="multiple" placeholder="All Departments" options={departmentOptions}
                  value={selectedDepartments} onChange={setSelectedDepartments} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Date Range" layout="vertical" style={{ marginBottom: 0 }}>
                <RangePicker allowClear onChange={(dates) => setDateRange(dates)} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </div>

      <div style={{ padding: '0 16px 16px' }}>
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={filteredProjects.slice().sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on))}
          columns={columns}
        />
      </div>
    </Card>
  );
};

export default ProjectsPage;